/**
 * Shared state: localStorage['eg:compare'] — { savedAt, items }, where items is
 * an array of { handle, title, image, url } in selection order, at most 4.
 * The selection expires 72 hours after it was last changed.
 */
(function () {
  'use strict';

  var KEY = 'eg:compare';
  var LIMIT = 4;
  var TTL = 72 * 60 * 60 * 1000;

  function read() {
    try {
      var stored = JSON.parse(localStorage.getItem(KEY));
      // a missing timestamp (or the old array format) counts as expired
      if (!stored || !Array.isArray(stored.items) || !(Date.now() - stored.savedAt < TTL)) {
        localStorage.removeItem(KEY);
        return [];
      }
      return stored.items
        .filter(function (i) { return i && i.handle; })
        .slice(0, LIMIT);
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    var items = list.slice(0, LIMIT);
    try {
      if (items.length) localStorage.setItem(KEY, JSON.stringify({ savedAt: Date.now(), items: items }));
      else localStorage.removeItem(KEY);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('eg:compare:change', { detail: { list: items } }));
    return items;
  }

  function handlesOf(list) {
    return list.map(function (i) { return i.handle; });
  }

  var CHECK_SVG =
    '<svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">' +
    '<path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.8" fill="none"/></svg>';

  class CompareBar extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.slotsEl = this.querySelector('[data-cmp-slots]');
      this.counters = this.querySelectorAll('[data-cmp-count]');
      this.collapseBtn = this.querySelector('[data-cmp-collapse]');
      this.goEl = this.querySelector('[data-cmp-go]');
      this.clearBtn = this.querySelector('[data-cmp-clear]');
      this.compareUrl = this.getAttribute('data-compare-url') || '/pages/compare';
      this.slotEmptyLabel = this.getAttribute('data-slot-empty') || '';
      this.removeLabel = this.getAttribute('data-remove-label') || '';

      this._onPickChange = this._onPickChange.bind(this);
      this._onSlotsClick = this._onSlotsClick.bind(this);
      this._onCollapse = this._onCollapse.bind(this);
      this._onClear = this._onClear.bind(this);
      this._onStoreChange = this._onStoreChange.bind(this);
      this._syncPicks = this._syncPicks.bind(this);
      this._onStorage = (e) => { if (e.key === KEY) this._onStoreChange(); };

      document.addEventListener('change', this._onPickChange);
      if (this.slotsEl) this.slotsEl.addEventListener('click', this._onSlotsClick);
      if (this.collapseBtn) this.collapseBtn.addEventListener('click', this._onCollapse);
      if (this.clearBtn) this.clearBtn.addEventListener('click', this._onClear);
      document.addEventListener('eg:compare:change', this._onStoreChange);
      window.addEventListener('storage', this._onStorage);

      // cards are re-rendered after AJAX filtering/pagination — re-sync the checkboxes
      const grid = document.querySelector('product-list') || document.querySelector('.product-list__inner');
      if (grid && window.MutationObserver) {
        let t;
        this._observer = new MutationObserver(() => {
          clearTimeout(t);
          t = setTimeout(this._syncPicks, 120);
        });
        this._observer.observe(grid, { childList: true, subtree: true });
      }

      this.render();
      this._syncPicks();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onPickChange);
      document.removeEventListener('eg:compare:change', this._onStoreChange);
      window.removeEventListener('storage', this._onStorage);
      if (this.slotsEl) this.slotsEl.removeEventListener('click', this._onSlotsClick);
      if (this._observer) this._observer.disconnect();
      this._wired = false;
    }

    _onStoreChange() {
      this.render();
      this._syncPicks();
    }

    _onPickChange(event) {
      const input = event.target;
      if (!input || input.type !== 'checkbox') return;
      const pick = input.closest('[data-eg-cmp-pick]');
      if (!pick) return;

      let list = read();
      const known = handlesOf(list).indexOf(pick.dataset.handle) !== -1;
      if (input.checked) {
        if (list.length >= LIMIT || known) {
          input.checked = known;
          return;
        }
        list.push({
          handle: pick.dataset.handle,
          title: pick.dataset.title,
          image: pick.dataset.image,
          url: pick.dataset.url
        });
        // a new pick expands the bar, otherwise the product lands in the hidden part
        this.dataset.open = 'true';
        if (this.collapseBtn) this.collapseBtn.setAttribute('aria-expanded', 'true');
      } else {
        list = list.filter((i) => i.handle !== pick.dataset.handle);
      }
      write(list);
    }

    _onSlotsClick(event) {
      const btn = event.target.closest('.eg-cmpbar__x');
      if (!btn) return;
      const handle = btn.getAttribute('data-handle');
      write(read().filter((i) => i.handle !== handle));
    }

    _onCollapse() {
      const open = this.dataset.open !== 'false';
      this.dataset.open = open ? 'false' : 'true';
      if (this.collapseBtn) this.collapseBtn.setAttribute('aria-expanded', String(!open));
    }

    _onClear() {
      write([]);
    }

    _syncPicks() {
      const list = read();
      const hs = handlesOf(list);
      const full = list.length >= LIMIT;
      const picks = document.querySelectorAll('[data-eg-cmp-pick]');
      for (let i = 0; i < picks.length; i++) {
        const pick = picks[i];
        const input = pick.querySelector('input');
        if (!input) continue;
        const checked = hs.indexOf(pick.dataset.handle) !== -1;
        input.checked = checked;
        input.disabled = full && !checked;
        pick.style.opacity = input.disabled ? '.4' : '';
      }
    }

    render() {
      const list = read();

      for (let i = 0; i < this.counters.length; i++) {
        this.counters[i].textContent = list.length;
      }

      if (this.slotsEl) {
        let html = '';
        for (let s = 0; s < LIMIT; s++) {
          const item = list[s];
          if (item) {
            html +=
              '<div class="eg-cmpbar__slot">' +
              '<img src="' + item.image + '" alt="">' +
              '<button type="button" class="eg-cmpbar__x" data-handle="' + item.handle + '" aria-label="' + this.removeLabel + '">' +
              CHECK_SVG +
              '</button>' +
              '</div>';
          } else {
            html +=
              '<div class="eg-cmpbar__slot eg-cmpbar__slot--empty">' +
              '<span class="eg-cmpbar__drop">' + this.slotEmptyLabel + '</span>' +
              '</div>';
          }
        }
        this.slotsEl.innerHTML = html;
      }

      if (this.goEl) {
        if (list.length >= 2) {
          // carry the catalogue the shopper came from, so "back" returns there
          var backFrom = location.pathname.indexOf('/collections/') === 0
            ? '&back=' + encodeURIComponent(location.pathname)
            : '';
          this.goEl.href = this.compareUrl + '?p=' + handlesOf(list).join(',') + backFrom;
          this.goEl.classList.remove('is-disabled');
          this.goEl.removeAttribute('aria-disabled');
        } else {
          this.goEl.href = this.compareUrl;
          this.goEl.classList.add('is-disabled');
          this.goEl.setAttribute('aria-disabled', 'true');
        }
      }

      if (list.length > 0) {
        this.hidden = false;
        requestAnimationFrame(() => this.classList.add('is-open'));
      } else {
        this.classList.remove('is-open');
        setTimeout(() => { if (!read().length) this.hidden = true; }, 250);
      }
    }
  }

  if (window.customElements && !customElements.get('compare-bar')) {
    customElements.define('compare-bar', CompareBar);
  }

  function initComparePage() {
    var root = document.getElementById('eg-cmp');
    if (!root) return;
    var pool = root.querySelector('[data-eg-cmp-pool]');
    var grid = root.querySelector('[data-eg-cmp-grid]');
    if (!pool || !grid) return;

    var ROWS = 7;
    var backUrl = root.getAttribute('data-back-url') || '/';
    // the catalogue is passed in the URL; only same-site paths are accepted
    var backParam = new URLSearchParams(location.search).get('back');
    if (backParam && backParam.charAt(0) === '/' && backParam.charAt(1) !== '/') {
      backUrl = backParam;
      var backLink = root.querySelector('.eg-cmp__back');
      if (backLink) backLink.setAttribute('href', backUrl);
    }
    // swatch index picked per column, so rebuilding the grid keeps the user's colour
    var chosen = {};

    var cols = {};
    Array.prototype.forEach.call(pool.querySelectorAll('.eg-cmp__col'), function (col) {
      cols[col.dataset.handle] = col;
    });

    function paramHandles() {
      var m = location.search.match(/[?&]p=([^&]+)/);
      if (!m) return [];
      return decodeURIComponent(m[1]).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function knownOrdered(hs) {
      var seen = {};
      var out = [];
      hs.forEach(function (h) {
        if (cols[h] && !seen[h]) { seen[h] = 1; out.push(h); }
      });
      return out.slice(0, LIMIT);
    }

    function visibleHandles() {
      return Array.prototype.map.call(
        grid.querySelectorAll('.eg-cmp__head[data-handle]'),
        function (h) { return h.getAttribute('data-handle'); }
      );
    }

    function persist(hs) {
      // an unchanged selection is not rewritten, so viewing the page does not extend its lifetime
      if (handlesOf(read()).join(',') === hs.join(',')) return;
      write(hs.map(function (h) {
        var c = cols[h];
        return { handle: h, title: c.dataset.title, url: c.dataset.url, image: c.dataset.image };
      }));
    }

    function syncUrl(hs) {
      history.replaceState(null, '', location.pathname + '?p=' + hs.join(','));
    }

    // a swatch switches to a sibling product inside its own column, no page load:
    // image, title, price and links change, the spec rows stay as they are
    function selectSwatch(head, swatch) {
      head.querySelectorAll('.eg-cmp__swatch').forEach(function (s) { s.classList.remove('is-active'); });
      swatch.classList.add('is-active');

      var img = head.querySelector('[data-cmp-img]');
      if (img && swatch.dataset.img) {
        img.src = swatch.dataset.img;
        if (swatch.dataset.title) img.alt = swatch.dataset.title;
      }

      var title = head.querySelector('[data-cmp-title]');
      if (title) {
        if (swatch.dataset.title) title.textContent = swatch.dataset.title;
        if (swatch.dataset.url) title.setAttribute('href', swatch.dataset.url);
      }

      // the money filter emits markup (currency prefix span), so it is set as HTML
      var price = head.querySelector('[data-cmp-price]');
      if (price && swatch.dataset.price) price.innerHTML = swatch.dataset.price;

      var imgLink = head.querySelector('.eg-cmp__img');
      if (imgLink && swatch.dataset.url) imgLink.setAttribute('href', swatch.dataset.url);

      var cta = head.querySelector('[data-cmp-cta]');
      if (cta && swatch.dataset.url) cta.setAttribute('href', swatch.dataset.url);
    }

    function build(hs) {
      grid.innerHTML = '';
      grid.style.setProperty('--eg-cmp-cols', hs.length);

      hs.forEach(function (h, i) {
        var head = cols[h].querySelector('.eg-cmp__head').cloneNode(true);
        head.setAttribute('data-col', i);
        head.setAttribute('data-handle', h);
        grid.appendChild(head);

        var swatch = chosen[h] != null && head.querySelectorAll('.eg-cmp__swatch')[chosen[h]];
        if (swatch) selectSwatch(head, swatch);
      });

      for (var r = 0; r < ROWS; r++) {
        var rule = document.createElement('div');
        rule.className = 'eg-cmp__rule';
        grid.appendChild(rule);

        hs.forEach(function (h, i) {
          var cell = cols[h].querySelectorAll('.eg-cmp__cell')[r].cloneNode(true);
          cell.setAttribute('data-col', i);
          if (i === hs.length - 1) cell.classList.add('eg-cmp__cell--last');
          grid.appendChild(cell);
        });
      }
    }

    function apply(hs) {
      persist(hs);
      // comparing needs at least two products; replace() keeps "Back" from returning here
      if (hs.length < 2) {
        location.replace(backUrl);
        return;
      }
      build(hs);
      syncUrl(hs);
    }

    // collapsing: the heading is clickable in any column, the whole row toggles
    grid.addEventListener('click', function (event) {
      var head = event.target.closest('.eg-cmp__cell-head--toggle');
      if (!head) return;
      var cell = head.closest('.eg-cmp__cell');
      var open = head.getAttribute('aria-expanded') === 'true';
      grid.querySelectorAll('.eg-cmp__cell[data-row="' + cell.getAttribute('data-row') + '"]').forEach(function (node) {
        node.setAttribute('data-open', String(!open));
        var button = node.querySelector('.eg-cmp__cell-head--toggle');
        if (button) button.setAttribute('aria-expanded', String(!open));
      });
    });

    grid.addEventListener('click', function (event) {
      var swatch = event.target.closest('.eg-cmp__swatch');
      if (!swatch) return;
      var head = swatch.closest('.eg-cmp__head');
      chosen[head.getAttribute('data-handle')] =
        Array.prototype.indexOf.call(head.querySelectorAll('.eg-cmp__swatch'), swatch);
      selectSwatch(head, swatch);
    });

    // remove a product: the column disappears, the grid is rebuilt
    grid.addEventListener('click', function (event) {
      var button = event.target.closest('.eg-cmp__remove');
      if (!button) return;
      var handle = button.closest('.eg-cmp__head').getAttribute('data-handle');
      delete chosen[handle];
      apply(visibleHandles().filter(function (h) { return h !== handle; }));
    });

    // start: ?p= takes priority, otherwise the saved selection
    var initial = knownOrdered(paramHandles());
    if (!initial.length) initial = knownOrdered(handlesOf(read()));
    apply(initial);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initComparePage);
  } else {
    initComparePage();
  }
})();
